var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { InteractiveObject } from "../interactive-object.js";
import { Vec3Tools } from "../../geometry/vec3-tools.js";
/**
 * Icon defined by `InteractivePoint::ButtonId` enumeration.
 */
export var ButtonIconId;
(function (ButtonIconId) {
    ButtonIconId[ButtonIconId["Undefined"] = 0] = "Undefined";
    ButtonIconId[ButtonIconId["Image"] = 1] = "Image";
    ButtonIconId[ButtonIconId["Sphere"] = 2] = "Sphere";
    ButtonIconId[ButtonIconId["ImageScaled"] = 3] = "ImageScaled";
    ButtonIconId[ButtonIconId["CrossPyramid"] = 4] = "CrossPyramid";
    ButtonIconId[ButtonIconId["CrossPrism"] = 5] = "CrossPrism";
})(ButtonIconId || (ButtonIconId = {}));
/**
 * Represents interactive 3D point / marker.
 */
export class InteractivePoint extends InteractiveObject {
    /** Empty constructor. */
    constructor(theViewer, theObjId = "") {
        super(theViewer, theObjId);
        if (this._myObjId === "") {
            this._myObjId = "_InteractivePoint";
        }
        this._myViewer.interactivePointController.createPoint(this._myObjId, true);
        this._myViewer.interactiveContext.displayObjectList(`${this._myObjId}`, false);
    }
    /** Make a copy of existing object with new id */
    clone() { return new InteractivePoint(this._myViewer, super._clone()); }
    /** Return icon id. */
    get iconId() {
        return this._myViewer.interactivePointController != null
            ? this._myViewer.interactivePointController.getPointIconId(this._myObjId)
            : 0;
    }
    /** Set icon id. */
    set iconId(theIconId) {
        this._myViewer.interactivePointController.setPointIconId(this._myObjId, theIconId);
    }
    /** Return position in 3D. */
    get position() {
        return this._myViewer.interactivePointController.getPointPosition(this._myObjId);
    }
    /** Set position in 3D. */
    set position(thePosition) {
        if (isNaN(thePosition.x) || isNaN(thePosition.y) || isNaN(thePosition.z)
            || Vec3Tools.isInfinite(thePosition)) {
            console.error("Error: InteractivePoint invalid position", thePosition);
            return;
        }
        this._myViewer.interactivePointController.setPointPosition(this._myObjId, thePosition);
    }
    /** Return point size. */
    get size() {
        return this._myViewer.interactivePointController != null
            ? this._myViewer.interactivePointController.getPointSize(this._myObjId)
            : 0.0;
    }
    /** Set point size. */
    set size(theSize) {
        this._myViewer.interactivePointController.setPointSize(this._myObjId, theSize);
    }
    /** Return height. */
    get height() {
        return this._myViewer.interactivePointController != null
            ? this._myViewer.interactivePointController.getHeight(this._myObjId)
            : 0.0;
    }
    /** Set height */
    set height(theHeight) {
        this._myViewer.interactivePointController.setHeight(this._myObjId, theHeight);
    }
    /** Return thickness. */
    get thickness() {
        return this._myViewer.interactivePointController != null
            ? this._myViewer.interactivePointController.getThickness(this._myObjId)
            : 0.0;
    }
    /** Set thickness */
    set thickness(theThickness) {
        this._myViewer.interactivePointController.setThickness(this._myObjId, theThickness);
    }
    /**
     * Load image from given url asynchronously.
     * @param theFileUrl - url to image file.
     */
    loadImageUrlAsync(theFileUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            let aFilePath = this._myObjId.length > 0 && this._myObjId[0] == "/" ? this._myObjId
                : "/" + this._myObjId;
            const aFilename = theFileUrl.split("/").pop();
            if (aFilename != null) {
                aFilePath += "/" + aFilename;
            }
            yield this._myViewer.modelLoader.uploadUrl(theFileUrl, aFilePath, true);
            this.imageFilePath = aFilePath;
        });
    }
    /** Open image from given preloaded file.
     * file should be preloaded with `ModelLoader::uploadUrl` or `ModelLoader::preloadFile`.
     * theFilePath should contain correct image extension.
     * @param theFilePath - path to image file.
     */
    set imageFilePath(theFilePath) {
        this._myViewer.interactivePointController.setImagePath(this._myObjId, theFilePath);
    }
    /** Set normal */
    set normal(theNormal) {
        this._myViewer.interactivePointController.setNormal(this._myObjId, theNormal);
    }
    /** Set up direction */
    set up(theUpDirection) {
        this._myViewer.interactivePointController.setUp(this._myObjId, theUpDirection);
    }
}
